import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Form, notification, Spin } from 'antd'
import dayjs from 'dayjs'
import { Drawer, DrawerFooter } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { UnitForm } from '@/components/form/UnitForm'
import type { UnitFormValues } from '@/components/form/UnitForm'
import {
  LEVEL_STRING_TO_NUMBER,
  createUnit,
  updateUnit,
  getUnitById,
} from '@/api/units.api'
import { formatEndDate, formatStartDate } from '@/utils/format'

interface UnitFormDrawerProps {
  isOpen: boolean
  mode: 'create' | 'update'
  unitId: string | null
  onClose: () => void
}

export function UnitFormDrawer({
  isOpen,
  mode,
  unitId,
  onClose,
}: UnitFormDrawerProps) {
  const [form] = Form.useForm<UnitFormValues>()
  const queryClient = useQueryClient()

  const { data: unit, isLoading: isFetching } = useQuery({
    queryKey: ['unit-detail', unitId],
    queryFn: () => (unitId ? getUnitById(unitId) : Promise.resolve(null)),
    enabled: isOpen && mode === 'update' && !!unitId,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  useEffect(() => {
    if (isOpen) {
      if (mode === 'update' && unit) {
        const unitData = unit as any

        let parsedLevel = 2
        if (typeof unitData.level === 'string') {
          parsedLevel = LEVEL_STRING_TO_NUMBER[unitData.level] || 2
        } else if (typeof unitData.level === 'number') {
          parsedLevel = unitData.level
        }

        form.setFieldsValue({
          id: unitData.id,
          name: unitData.name,
          code: unitData.code,
          level: parsedLevel,
          phoneNumber: unitData.phoneNumber,
          startDate: unitData.startDate ? dayjs(unitData.startDate) : undefined,
          endDate: unitData.endDate ? dayjs(unitData.endDate) : undefined,
          fullNameAdmin: unitData.fullNameAdmin,
          phoneNumberAdmin: unitData.phoneNumberAdmin,
          personalEmailAdmin: unitData.personalEmailAdmin,
          note: unitData.description,
          email: unitData.emailAdmin,
        })
      } else if (mode === 'create') {
        form.resetFields()
        form.setFieldsValue({ level: 2 })
      }
    } else {
      form.resetFields()
    }
  }, [isOpen, mode, unit, form])

  const createMutation = useMutation({
    mutationFn: createUnit,
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Thêm mới đơn vị thành công!'
      notification.success({ title: successMessage })

      queryClient.invalidateQueries({ queryKey: ['units'] })
      onClose()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (values: any) => {
      if (!unitId) throw new Error('Missing unit ID')
      return updateUnit(unitId, {
        ...values,
        status: unit?.status ?? 1,
      })
    },
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Cập nhật đơn vị thành công!'
      notification.success({ title: successMessage })

      queryClient.invalidateQueries({ queryKey: ['units'] })
      if (unitId) {
        queryClient.invalidateQueries({ queryKey: ['unit-detail', unitId] })
      }
      onClose()
    },
  })

  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const isDrawerDisabled = isSubmitting || isFetching

  const handleSubmit = (values: UnitFormValues) => {
    const payload: any = {
      name: values.name,
      code: values.code,
      level: values.level,
      phoneNumber: values.phoneNumber,
      startDate: formatStartDate(values.startDate) || undefined,
      endDate: formatEndDate(values.endDate) || undefined,
      fullNameAdmin: values.fullNameAdmin,
      phoneNumberAdmin: values.phoneNumberAdmin,
      personalEmailAdmin: values.personalEmailAdmin,
      description: values.note,
      emailAdmin: values.email,
    }

    if (mode === 'create') {
      payload.emailAdmin = values.email
      createMutation.mutate(payload)
    } else {
      updateMutation.mutate(payload)
    }
  }

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'THÊM MỚI ĐƠN VỊ' : 'CẬP NHẬT ĐƠN VỊ'}
      styles={{ body: { overflow: 'auto', paddingBottom: '80px' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={isDrawerDisabled}
            className="min-w-[160px] h-10"
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => form.submit()}
            disabled={isDrawerDisabled}
            className="min-w-[160px] h-10"
          >
            {isSubmitting ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </DrawerFooter>
      }
    >
      <Spin spinning={isFetching} description="Đang tải dữ liệu...">
        <div className="min-h-[300px]">
          {(mode === 'create' || unit) && (
            <UnitForm form={form} mode={mode} onSubmit={handleSubmit} />
          )}
        </div>
      </Spin>
    </Drawer>
  )
}
